/**
 * Los ids de la base de conocimiento: DETERMINISTAS (la misma cosa da el mismo
 * id en dos sesiones) y, sobre todo, DISTINTOS para cosas distintas.
 *
 * POR QUÉ EXISTE ESTE MÓDULO. Los dos archivos de datos derivaban el id de un
 * `slug` que aplastaba toda corrida de caracteres no alfanuméricos a un guion
 * (`texto.replace(/[^a-z0-9]+/g, "-")`). Eso hacía colisionar cosas que NO son
 * la misma:
 *  - `instagram.com/gary.vee` e `instagram.com/gary_vee` son dos usuarios
 *    válidos y distintos, y daban el mismo id: entraban las dos a la lista (el
 *    dedup de canales es por URL, no por id) y la ✕ de una borraba LAS DOS,
 *    porque `quitarFuente` filtra por id;
 *  - `ejemplo.com/a-b` y `ejemplo.com/a/b` son dos páginas, y daban el mismo
 *    id: como el dedup de materiales SÍ es por id, la segunda no entraba y la
 *    pantalla decía "esa página ya está en la lista", que era falso;
 *  - `informe v2.pdf` e `informe-v2.pdf` del mismo tamaño, lo mismo.
 *
 * La forma del id es `prefijo-parteLegible-hash`: la parte legible ayuda a
 * mirar el `localStorage` con ojo humano, y el HASH —calculado sobre la clave
 * ENTERA, sin aplastar nada— es el que garantiza que dos claves distintas den
 * ids distintos. La parte legible además va RECORTADA, así que un pegado
 * absurdo no produce un id de miles de caracteres que después se serializa en
 * cada escritura.
 *
 * Ver ADR-2026-08-17-estudio-marketing-ids-sin-colision.
 */

/**
 * Hash determinista (djb2) en base 36. Sin `Date.now()` ni `Math.random()`:
 * dos sesiones que cargan la misma fuente tienen que producir el mismo id para
 * que lo guardado en el navegador se pueda comparar y deduplicar.
 */
export function hashTexto(texto: string): string {
  let hash = 5381;
  for (let i = 0; i < texto.length; i += 1) {
    hash = ((hash << 5) + hash + texto.charCodeAt(i)) | 0;
  }
  return (hash >>> 0).toString(36);
}

/** Cuánto de la clave se conserva en claro. Es cosmético: lo que distingue un
 *  id de otro es el hash, que se calcula sobre la clave completa. */
const MAX_LEGIBLE = 40;

const legible = (clave: string) =>
  clave
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, MAX_LEGIBLE)
    .replace(/-+$/g, "");

/**
 * Un id estable para `clave`. Dos claves distintas dan ids distintos: lo que
 * decide es el hash de la clave entera, no el trozo legible.
 *
 * La clave tiene que ser la forma CANÓNICA de la cosa (la URL ya normalizada,
 * el nombre del archivo con su tamaño): dos escrituras de lo mismo que se
 * consideran duplicadas tienen que llegar acá con la misma clave.
 */
export function idEstable(prefijo: string, clave: string): string {
  const parte = legible(clave);
  return parte ? `${prefijo}-${parte}-${hashTexto(clave)}` : `${prefijo}-${hashTexto(clave)}`;
}
